/**
 * Themed specialty picker. Pulls labels from `data/specialties.ts` so the
 * profile picker, the onboarding modal, and any future filter UI render
 * the same canonical list. Uses `BrandSelect` for app-aligned styling
 * — replaces the prior native `<select>` so the dropdown matches the
 * settings-card idiom instead of the browser default.
 */

import React, { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { SPECIALTIES } from '@/data/specialties';
import { BrandSelect } from '@/components/ui/BrandSelect';

interface Props {
  value: string | null | undefined;
  onChange: (next: string | null) => void;
  /** Render a leading "Not set" entry that resolves to null. */
  allowEmpty?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
}

export const SpecialtyPicker: React.FC<Props> = ({
  value,
  onChange,
  allowEmpty = true,
  disabled,
  id,
  className,
}) => {
  const { t, language } = useTranslation();
  const lang = language === 'ar' ? 'ar' : 'en';

  const options = useMemo(
    () => SPECIALTIES.map((s) => ({ value: s.value, label: s.label[lang] })),
    [lang],
  );

  return (
    <BrandSelect
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      allowEmpty={allowEmpty}
      emptyLabel={t('profile.specialty.placeholder')}
      placeholder={t('profile.specialty.placeholder')}
      disabled={disabled}
      className={className}
      ariaLabel={t('profile.specialty.label') || 'Specialty'}
    />
  );
};
